/**
 * VoyageProgressCard - Track current position, speed, course, and ETA
 */

import React from 'react';
import { Navigation, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const VoyageProgressCard = ({ 
  currentPosition,
  setCurrentPosition,
  activeVoyage,
  isExpanded,
  onToggle,
  onAddLogEntry
}) => {
  const isComplete = currentPosition.lat && currentPosition.lon;

  const handleSavePosition = () => {
    if (!currentPosition.lat || !currentPosition.lon) {
      alert('Please enter latitude and longitude');
      return;
    }

    // Create position log entry
    const positionEntry = {
      type: 'POSITION',
      category: 'navigation',
      summary: `Position: ${currentPosition.lat}, ${currentPosition.lon} • ${currentPosition.speed || '0'} kts • ${currentPosition.course || '000'}°`,
      details: {
        position: {
          lat: currentPosition.lat,
          lon: currentPosition.lon
        },
        speed: currentPosition.speed,
        course: currentPosition.course,
        eta: currentPosition.eta
      },
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };

    onAddLogEntry(positionEntry);
  };

  return (
    <div className={`rounded-lg border-2 transition-all ${
      isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
    }`}>
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Navigation className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-gray-600'}`} />
            <div>
              <h4 className="text-sm font-medium">Voyage Progress</h4>
              <p className="text-xs text-gray-500">
                {currentPosition.lat && currentPosition.lon 
                  ? `${currentPosition.lat}, ${currentPosition.lon} • ${currentPosition.speed || '0'} kts`
                  : 'No position recorded'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Position */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Latitude
                </label>
                <input
                  type="text"
                  value={currentPosition.lat}
                  onChange={(e) => setCurrentPosition(prev => ({ ...prev, lat: e.target.value }))}
                  placeholder="e.g., 50.3755 N"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Longitude
                </label>
                <input
                  type="text"
                  value={currentPosition.lon}
                  onChange={(e) => setCurrentPosition(prev => ({ ...prev, lon: e.target.value }))}
                  placeholder="e.g., 004.1427 W"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Speed */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Speed (knots)
                </label>
                <input
                  type="number"
                  value={currentPosition.speed}
                  onChange={(e) => setCurrentPosition(prev => ({ ...prev, speed: e.target.value }))}
                  placeholder="0.0"
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Course */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Course (°)
                </label>
                <input
                  type="number"
                  value={currentPosition.course}
                  onChange={(e) => setCurrentPosition(prev => ({ ...prev, course: e.target.value }))}
                  placeholder="000"
                  min="0"
                  max="360"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* ETA */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  ETA
                </label>
                <input
                  type="time"
                  value={currentPosition.eta}
                  onChange={(e) => setCurrentPosition(prev => ({ ...prev, eta: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Distance to Destination */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-xs text-blue-700">Distance to {activeVoyage?.destinationPort}</span>
                <span className="text-sm font-medium text-blue-900">
                  {activeVoyage?.remainingDistance || 'Calculate'} NM
                </span>
              </div>
            </div>

            {/* Save Position Button */}
            <button
              onClick={handleSavePosition}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Log Position Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoyageProgressCard;